/**
 * SCHEDULE — the phone (migration 3071; DESIGN-CHANGELOG 2026-10-14).
 * `design/GT Mobile Schedule.dc.html`, the `inScreen` half:
 *
 *  - `Schedule` — the month as a 7-column grid: date, headcount, tiny covers.
 *  - `SchedNight` — the night page, full screen (`MobileApp` hides the header
 *    and tab bar on `isSchedDay`); `◂ SCHEDULE` is the way out. START (tonight
 *    only), then the guide — it scrolls sideways with the player column pinned
 *    and opens at NOW; a block's text and faces park inside the visible edge
 *    — then + NOMINATE.
 *
 * THE PARK IS WRITTEN TO THE DOM, NEVER TO STATE (the mirror's own lesson,
 * 2026-10-14): a re-render per scroll frame jittered the pinned column. So the
 * scroller's listener moves each block's text strip directly, and a layout
 * effect re-applies it after any render that rebuilt the blocks.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';

import { Avatar } from '../components/Avatar.tsx';
import { PixelButton } from '../components/PixelButton.tsx';
import { SCH_SCRIM, SchCover, SchFaceStack, useSchedClock } from '../components/SchedKit.tsx';
import { useQueueArt } from '../components/useArt.ts';
import { useQd } from '../store/index.ts';
import { SCH_GUT_M, scheduleVM } from '../store/selectors.schedule.ts';
import type { SchNightVM } from '../store/selectors.schedule.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';
const SANS = 'var(--gt-font-sans)';

/** How wide a block's text strip is at most, and the strip's floor. */
const STRIP = 120;

export default function Schedule(): JSX.Element {
  const s = useQd();
  const now = useSchedClock();
  const v = scheduleVM(s, now);
  const { refreshSched, schY, schM } = s;
  useEffect(() => {
    void refreshSched();
  }, [refreshSched, schY, schM]);
  useQueueArt(Object.values(s.sched).flat().map((n) => n.id));

  return (
    <div data-screen-label="Schedule" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 14px 0' }}>
        <div style={{ display: 'flex', fontFamily: MONO, fontSize: 10 }}>
          <div onClick={() => s.schMonthGo(-1)} style={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 40, border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-5)', boxSizing: 'border-box', cursor: 'pointer' }}>
            ◂
          </div>
          <div style={{ flex: 1, position: 'relative', zIndex: 2, marginLeft: -1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40, border: '1px solid var(--acc)', color: 'var(--acc)', background: 'var(--accSoft)', boxSizing: 'border-box', whiteSpace: 'nowrap' }}>
            {v.monthTxt}
          </div>
          <div onClick={() => s.schMonthGo(1)} style={{ flex: 'none', marginLeft: -1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 40, border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-5)', boxSizing: 'border-box', cursor: 'pointer' }}>
            ▸
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.05em' }}>
          <span style={{ border: '1px solid var(--acc)', background: 'var(--accSoft)', padding: '7px 8px', color: 'var(--acc)' }}>TONIGHT · {v.tonightTxt}</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 14px 20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ display: 'flex', gap: 3 }}>
            {v.head.map((h) => (
              <div key={h.key} style={{ flex: 1, minWidth: 0, padding: '0 0 4px', borderBottom: '1px solid var(--gt-line)', fontFamily: MONO, fontSize: '7.5px', letterSpacing: '.08em', color: h.clr, textAlign: 'center' }}>
                {h.l1}
              </div>
            ))}
          </div>
          {v.weeks.map((w) => (
            <div key={w.key} style={{ display: 'flex', gap: 3 }}>
              {w.cells.map((c) => (
                <div
                  key={c.key}
                  onClick={c.can ? () => s.schOpenDay(c.k) : undefined}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    boxSizing: 'border-box',
                    height: 76,
                    border: c.bd,
                    background: c.bg,
                    boxShadow: c.sh,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '7px 3px 6px',
                    cursor: c.cur,
                    position: 'relative',
                    zIndex: c.z,
                    opacity: c.op,
                  }}
                >
                  {c.today ? <div style={{ position: 'absolute', left: -1, right: -1, top: -1, height: 3, background: 'var(--acc)' }} /> : null}
                  <span style={{ fontFamily: PIXEL, fontSize: c.nFsM, color: c.nClr, transform: 'translateY(0.13em)' }}>{c.n}</span>
                  <span style={{ fontFamily: MONO, fontSize: '6.5px', letterSpacing: '.04em', color: 'var(--gt-muted-2)', whiteSpace: 'nowrap' }}>{c.sum}</span>
                  <div style={{ display: 'flex', gap: 2, height: 11 }}>
                    {c.minis.map((mk) => (
                      <SchCover key={mk.key} id={mk.id} cands={mk.coverCands} fall={mk.fall} title={mk.nm} style={{ width: 8, height: 11, boxSizing: 'border-box', border: '1px solid var(--gt-ink)' }} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{ fontFamily: MONO, fontSize: '7.5px', letterSpacing: '.07em', lineHeight: 1.8, color: 'var(--gt-muted-2)' }}>
          NUMBER UNDER THE DATE = WHO CAN PLAY · TINY COVERS = NOMINATIONS · TAP A NIGHT TO OPEN IT
        </div>
      </div>
    </div>
  );
}

export function SchedNight(): JSX.Element | null {
  const s = useQd();
  const now = useSchedClock();
  const v = scheduleVM(s, now);
  const d = v.day;
  useQueueArt((d ? s.sched[d.k] ?? [] : []).map((n) => n.id));
  if (!d) return null;

  return (
    <div data-screen-label="Schedule night" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: '100vh', background: 'var(--gt-bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: '1px solid var(--gt-line)', background: 'var(--gt-panel)', position: 'sticky', top: 0, zIndex: 20 }}>
        <div
          onClick={() => s.schBack()}
          style={{ display: 'flex', gap: 7, alignItems: 'center', justifyContent: 'center', height: 44, padding: '0 11px', border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-4)', fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.05em', whiteSpace: 'nowrap', flex: 'none', boxSizing: 'border-box', cursor: 'pointer' }}
        >
          ◂ SCHEDULE
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontFamily: PIXEL, fontSize: '9.5px', lineHeight: 1.6, color: 'var(--gt-text)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap' }}>{d.ttl}</span>
            {d.isToday ? (
              <span style={{ fontFamily: PIXEL, fontSize: 6, lineHeight: 1, color: 'var(--gt-ink)', background: 'var(--acc)', padding: '3px 4px 2px', whiteSpace: 'nowrap' }}>TONIGHT</span>
            ) : null}
          </div>
          <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', color: 'var(--gt-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.meta}</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 14px 26px' }}>
        {d.isToday ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <PixelButton label="START GAME NIGHT ▸" tone="acc" variant={d.startVar} fs={10} pad="17px 14px" center lh="1" busy={s.rBusy} onClick={() => void s.schStart()} />
            <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', color: 'var(--gt-muted-2)', textAlign: 'center' }}>{d.startSub}</span>
          </div>
        ) : null}
        {d.notToday || d.isPast ? (
          <div style={{ border: '1px dashed var(--gt-border-3)', padding: 12, fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', lineHeight: 1.9, color: 'var(--gt-muted)' }}>{d.laterTxt}</div>
        ) : null}
        <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', color: 'var(--gt-muted-2)', paddingTop: 4 }}>{d.nomHead}</span>
        {d.nomEmpty ? (
          <div style={{ border: '1px dashed var(--gt-border-3)', padding: 12, fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.07em', color: 'var(--gt-muted-2)' }}>NOTHING NOMINATED YET</div>
        ) : null}
        {d.nomOn ? <Guide d={d} /> : null}
        {/* A night that has passed takes no new nominations — the server
            refuses them, so the button would only ever say no. */}
        {d.isPast ? null : <PixelButton label="+ NOMINATE" fs={9} pad="16px 14px" center lh="1" variant="outline" onClick={() => s.schNomStart()} />}
      </div>
    </div>
  );
}

/** Park every block's text strip inside the visible edge — straight to the DOM. */
function park(el: HTMLElement): void {
  const sl = el.scrollLeft;
  const right = sl + Math.max(STRIP, el.clientWidth - SCH_GUT_M);
  el.querySelectorAll<HTMLElement>('[data-bl]').forEach((strip) => {
    const bl = Number(strip.dataset.bl);
    const bw = Number(strip.dataset.bw);
    const shift = Math.max(0, Math.min(sl - bl, bw - STRIP));
    const w = Math.max(Math.min(bw, STRIP), Math.min(bl + bw, right) - (bl + shift));
    strip.style.marginLeft = shift + 'px';
    strip.style.width = w + 'px';
  });
}

/** The phone guide: sideways scroll, the player column pinned, opened at NOW. */
function Guide({ d }: { d: SchNightVM }): JSX.Element {
  const open = useQd((s) => s.schOpenGame);
  const ref = useRef<HTMLDivElement | null>(null);
  const openAt = useRef(d.openAt);
  openAt.current = d.openAt;

  // Once per night page: open at NOW, then keep the strips parked on scroll,
  // and let a mouse drag pan the hidden-bar scroller (a real drag swallows the
  // click under it, so letting go over a block does not open it).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let n = 0;
    let raf = 0;
    const go = (): void => {
      if (el.scrollWidth > el.clientWidth + 4) {
        el.scrollLeft = openAt.current;
        park(el);
        return;
      }
      if (++n < 120) raf = requestAnimationFrame(go);
    };
    raf = requestAnimationFrame(go);
    const onScroll = (): void => park(el);
    el.addEventListener('scroll', onScroll, { passive: true });
    let on = false;
    let moved = false;
    let sx = 0;
    let sl0 = 0;
    let swallow = 0;
    const down = (e: PointerEvent): void => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      on = true;
      moved = false;
      sx = e.clientX;
      sl0 = el.scrollLeft;
    };
    const move = (e: PointerEvent): void => {
      if (!on) return;
      const dx = e.clientX - sx;
      if (Math.abs(dx) > 6) moved = true;
      if (moved) {
        el.scrollLeft = sl0 - dx;
        e.preventDefault();
      }
    };
    const end = (): void => {
      if (!on) return;
      on = false;
      if (moved) swallow = Date.now();
    };
    const click = (e: MouseEvent): void => {
      if (swallow && Date.now() - swallow < 400) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('pointerleave', end);
    el.addEventListener('click', click, true);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', end);
      el.removeEventListener('pointercancel', end);
      el.removeEventListener('pointerleave', end);
      el.removeEventListener('click', click, true);
    };
  }, []);

  // A render that rebuilt the blocks put their strips back at rest — re-park.
  useLayoutEffect(() => {
    if (ref.current) park(ref.current);
  });

  return (
    <div data-hs="1" ref={ref} style={{ overflowX: 'auto', position: 'relative', cursor: 'grab' }}>
      <div style={{ width: d.gW, position: 'relative' }}>
        <div style={{ display: 'flex', height: 26, alignItems: 'flex-end' }}>
          <div style={{ width: SCH_GUT_M, flex: 'none', position: 'sticky', left: 0, zIndex: 4, background: 'var(--gt-bg)', boxShadow: '-6px 0 0 var(--gt-bg)', height: '100%' }} />
          <div style={{ flex: 1, position: 'relative', height: '100%' }}>
            {d.gHours.map((k) => (
              <span key={k.key} style={{ position: 'absolute', left: k.left, bottom: 6, transform: k.tx, fontFamily: MONO, fontSize: 7, letterSpacing: '.05em', color: k.clr, whiteSpace: 'nowrap' }}>
                {k.l}
              </span>
            ))}
            {d.nowOn ? (
              <div style={{ position: 'absolute', left: d.nowX, bottom: 2, transform: 'translateX(-50%)', background: 'var(--acc)', color: 'var(--gt-ink)', fontFamily: MONO, fontSize: '7.5px', fontWeight: 600, letterSpacing: '.06em', padding: '3px 6px', whiteSpace: 'nowrap', zIndex: 3 }}>
                {d.nowPill}
              </div>
            ) : null}
          </div>
        </div>
        {d.rows.map((r) => (
          <div key={r.key} style={{ display: 'flex', borderTop: '1px solid var(--gt-border)' }}>
            <div
              style={{
                width: SCH_GUT_M,
                flex: 'none',
                position: 'sticky',
                left: 0,
                zIndex: 4,
                background: 'var(--gt-bg)',
                boxShadow: '-6px 0 0 var(--gt-bg)',
                borderRight: '1px solid var(--gt-border)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                padding: '6px 4px',
                boxSizing: 'border-box',
              }}
            >
              <Avatar size={20} fs={7} ini={r.ini} bg={r.bg} hasAv={r.hasAv} avBg={r.avBg} />
              <span style={{ fontFamily: MONO, fontSize: 7, letterSpacing: '.08em', color: r.clr, textAlign: 'center', lineHeight: 1.5 }}>{r.nm}</span>
            </div>
            <div style={{ flex: 1, position: 'relative', minHeight: r.h, alignSelf: 'stretch' }}>
              {d.gHours.map((k) => (
                <div key={k.key} style={{ position: 'absolute', top: 0, bottom: 0, left: k.left, borderLeft: '1px ' + k.st + ' var(--gt-line-0)' }} />
              ))}
              {d.nowOn ? <div style={{ position: 'absolute', top: 0, bottom: 0, left: d.nowX, borderLeft: '1px solid var(--acc)', zIndex: 3, pointerEvents: 'none' }} /> : null}
              {r.blocks.map((n) => (
                <SchCover
                  key={n.key}
                  id={n.id}
                  cands={n.coverCands}
                  fall={n.fall}
                  onClick={() => open(n.id)}
                  style={{
                    position: 'absolute',
                    left: n.left,
                    top: n.top,
                    width: n.w,
                    height: 56,
                    boxSizing: 'border-box',
                    border: '1px solid ' + n.bd,
                    borderRight: '1px ' + n.bRst + ' ' + n.bd,
                    overflow: 'hidden',
                    opacity: n.op,
                    zIndex: 2,
                    boxShadow: n.sh,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ position: 'absolute', inset: 0, background: SCH_SCRIM }} />
                  <div style={{ position: 'relative', zIndex: 1, display: 'flex', height: '100%', boxSizing: 'border-box' }}>
                    <div data-bl={n.blPx} data-bw={n.bwPx} style={{ flex: 'none', width: n.stripW, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '7px 8px', boxSizing: 'border-box' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <span style={{ fontFamily: SANS, fontSize: 12, fontWeight: 500, color: 'var(--gt-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.nm}</span>
                        <span style={{ fontFamily: PIXEL, fontSize: 7, color: n.tClr, transform: 'translateY(0.13em)' }}>{n.tLet}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {n.rangeOnM ? <span style={{ fontFamily: MONO, fontSize: '7.5px', letterSpacing: '.05em', color: n.rClr, whiteSpace: 'nowrap' }}>{n.rangeLive}</span> : null}
                        <div style={{ marginLeft: 'auto' }}>
                          <SchFaceStack faces={n.qFaces} />
                        </div>
                      </div>
                    </div>
                  </div>
                </SchCover>
              ))}
            </div>
          </div>
        ))}
        <div style={{ borderTop: '1px solid var(--gt-border)' }} />
      </div>
    </div>
  );
}
